import * as yup from "yup";

// ServiceAccount や ServiceAccountKey が満たすべき制約のうち、
// 画面からの操作によって起こりうる入力不備にのみフォーカスしたバリデーションを提供する。
// サーバーサイドでは、より厳密なバリデーションが実行されている。

export const validateCreateServiceAccount: ValidationFunction<
  typeof createServiceAccountSchema
> = (input) => validateWithSchema(input, createServiceAccountSchema);

export const validateUpdateServiceAccount: ValidationFunction<
  typeof updateServiceAccountSchema
> = (input) => validateWithSchema(input, updateServiceAccountSchema);

export const validateCreateServiceAccountKey: ValidationFunction<
  typeof createServiceAccountKeySchema
> = (input) => validateWithSchema(input, createServiceAccountKeySchema);

type ValidationFunction<T extends yup.ObjectSchema<yup.AnyObject>> = (
  input: Record<string, unknown>,
) => ValidationResult<T>;

type ValidationResult<T extends yup.ObjectSchema<yup.AnyObject>> = {
  [key in keyof T["fields"]]?: string;
};

const validateWithSchema = <T extends yup.ObjectSchema<yup.AnyObject>>(
  input: Record<string, unknown>,
  schema: T,
): ValidationResult<T> => {
  try {
    schema.validateSync(input, { abortEarly: false, strict: true });
    return {};
  } catch (e) {
    // 予期しないエラー内容だった場合 ErrorBoundary まで throw してしまう
    if (!(e instanceof yup.ValidationError) || e.inner.length === 0) {
      throw new Error(
        `Unexpected error detected on validating schema, error name ${e.name}, original message ${e.message}`,
      );
    }

    const result: Record<string, string> = {} satisfies ValidationResult<T>;
    e.inner.forEach(({ path, message }) => {
      if (isExpectedKey(path, schema)) {
        result[path] = message;
        return;
      }
      throw new Error(
        `Unexpected error in ValidationError.inner detected on validating schema, error path ${path}, message ${message}`,
      );
    });
    return result;
  }
};

const isExpectedKey = <T extends yup.ObjectSchema<yup.AnyObject>>(
  key: unknown,
  schema: T,
): key is keyof T["fields"] => {
  return typeof key === "string" && key in schema.fields;
};

const baseServiceAccountSchema = yup.object({
  description: yup
    .string()
    .optional()
    .max(256, "256文字以下で入力してください。"),
  roles: yup
    .array()
    .required("ロールを選択してください。")
    .min(1, "ロールを選択してください。"),
  groupsRoles: yup
    .object()
    .required() // 所属がない場合も empty な object が入る
    .test("group-is-selected", "グループを選択してください。", (obj) =>
      Object.keys(obj).every((key) => !!key),
    )
    .when("roles", {
      is: (roles: unknown) => Array.isArray(roles) && roles.includes("Staff"),
      then: (schema) =>
        schema.test(
          "group-role-exists-if-staff",
          "一般ユーザーロールを割り当てる場合、グループをひとつ以上選択してください。",
          (obj) => Object.keys(obj).length > 0,
        ),
    }),
});

const createServiceAccountSchema = baseServiceAccountSchema.shape({
  accountName: yup
    .string()
    .required("アカウント名を入力してください。")
    .max(256, "アカウント名は256文字以下で入力してください。")
    .matches(
      /^[a-zA-Z0-9!#$%&'+/=?^_`{|}~@.-]*$/,
      "アカウント名は半角英数字と記号のみ使用可能です。",
    ),
});

const updateServiceAccountSchema = baseServiceAccountSchema.shape({
  // nothing to add for now
});

const createServiceAccountKeySchema = yup.object({
  name: yup.string().optional().max(20, "20文字以下で入力してください。"),
});
